import { useMemo } from "react";
import { useApp } from "../lib/store";

/** The players you watch, as a calm card grid. Used wherever we want to show
 *  the whole watch set rather than only the pros currently detected in a lobby:
 *   - status "idle": between matches, nothing live.
 *   - status "pending": a window is live (or being viewed) but no pro is
 *     detected yet — we still surface the full roster for visibility.
 *  Each card carries its own remove, so the watchlist stays editable even when
 *  nothing is live (otherwise the only remove lives on a live team card). */
export function Roster({ status }: { status: "idle" | "pending" }) {
  const { watchedIds, candidates, unwatchPlayer } = useApp();
  const byId = useMemo(
    () => new Map(candidates.map(c => [c.epic_id, c] as const)),
    [candidates],
  );
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {watchedIds.map((id, i) => {
        const c = byId.get(id);
        const name = c?.username ?? "Unknown pro";
        return (
          <div
            key={id}
            className="fade-up chamfer flex items-center justify-between gap-2 bg-surface py-3.5 pl-4 pr-2.5"
            style={{ animationDelay: `${Math.min(i, 10) * 40}ms` }}
          >
            <div className="min-w-0">
              <p className="display truncate text-[14.5px] text-fg">{name}</p>
              <p className="mt-0.5 truncate font-mono text-[11px] text-faint">{c?.region ?? ""}</p>
            </div>
            <div className="flex shrink-0 items-center gap-1.5">
              <span className="label-xs">{status}</span>
              <button
                onClick={() => unwatchPlayer(id)}
                title={`Remove ${name} from your players`}
                aria-label={`Remove ${name} from your players`}
                className="press rounded-md p-1 text-faint transition-colors hover:bg-raised hover:text-err"
              >
                <RemoveGlyph />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function RemoveGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-3.5 w-3.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}
